import { DefaultGeneralSpecialty } from '~/constants';
import type { IUserCredentials } from '~/models/UserCredentials';
import type { IUser } from '~/models/User';
import { ParkingCostEnum } from '~/models/ParkingInformation';
import { SoftwareUtils } from '~/utils/SoftwareUtils';
import type { ILocationSectionValues } from '../types/LocationSettings';
import { splitXraySoftware } from './xraySoftware';

/**
 * Each section's starting values, read from the office record and its
 * credentials with the same fallbacks Account Information used
 * (`useAccountInformation.js` `defaultValues`).
 */
export const toSectionDefaults = (
  office: IUser,
  credentials: IUserCredentials | null | undefined,
): ILocationSectionValues => {
  const productivity = office.office_info?.productivity;
  const displaySoftwares: string[] = SoftwareUtils.getSoftwareTranslations(
    credentials?.education?.softwares,
  );
  const { software, xray } = splitXraySoftware(displaySoftwares);

  return {
    basicInformation: {
      officeName: office.office_name || '',
      specialty:
        Object.keys(credentials?.education?.specialtiesAndProcedures || {})[0] ||
        DefaultGeneralSpecialty,
      officeType: office.office_info?.officeType || '',
      operatories: productivity?.operatories || null,
    },
    contact: {
      website: office.web_site || '',
    },
    addressParking: {
      isParkingFree: office.parkingInformation?.isParkingFree,
      parkingCost: office.parkingInformation?.cost ? String(office.parkingInformation.cost) : '',
      parkingCostType: office.parkingInformation?.costType || ParkingCostEnum.Hourly,
      parkingType: office.parkingInformation?.parkingType || '',
    },
    shiftDetails: {
      timePerPatientRDH: office.office_info?.timePerPatientRDH || '',
      timePerPatientDA: office.office_info?.timePerPatientDA || '',
      timePerPatientDN: office.office_info?.timePerPatientDN || '',
      isAssistedHygiene: office.office_info?.isAssistedHygiene,
      notes: office.notes_for_temp || '',
    },
    insuranceSoftware: {
      insurances: credentials?.insurances || [],
      softwares: software,
      xraySoftwares: xray,
    },
    operatingDetails: {
      permanentStaff: {
        RDH: productivity?.permanentStaff?.RDH || null,
        DA: productivity?.permanentStaff?.DA || null,
        DN: productivity?.permanentStaff?.DN || null,
      },
      bookedOutPatients: productivity?.bookedOutPatients || null,
      partnershipOrganization: office.partnerReferral?.partnershipCode?.organization || '',
      partnershipCode: office.partnerReferral?.partnershipCode?.code || '',
    },
  };
};
